import { useState } from 'react';
import {
  HelpCircle,
  Keyboard,
  BookOpen,
  LifeBuoy,
  Volume2,
  ExternalLink,
} from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { playNotificationSound } from '@/shared/utils/sound';

export interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

export function HelpModal({ open, onClose }: HelpModalProps) {
  const navigate = useNavigate();
  const [testedSound, setTestedSound] = useState(false);

  const handleTestChime = () => {
    setTestedSound(true);
    playNotificationSound(true);
    setTimeout(() => setTestedSound(false), 900);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-200/60">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div>
            <span className="font-bold text-base text-slate-900">Help & Command Center Guide</span>
            <p className="text-xs font-normal text-slate-500">Quick shortcuts, guides, and IT support</p>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-slate-400">ZYNX Enterprise v2.0</span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition"
          >
            Close
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Keyboard Shortcuts Card */}
        <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-800 mb-2.5">
            <Keyboard className="w-4 h-4 text-blue-600" />
            <span>Essential Keyboard Shortcuts</span>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Global Search & Command Palette</span>
              <div className="flex gap-1">
                <kbd className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold text-slate-700 shadow-2xs">
                  ⌘ K
                </kbd>
                <span className="text-slate-400">or</span>
                <kbd className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold text-slate-700 shadow-2xs">
                  Ctrl+K
                </kbd>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-600">Close Open Modals & Drawers</span>
              <kbd className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold text-slate-700 shadow-2xs">
                Esc
              </kbd>
            </div>
          </div>
        </div>

        {/* Quick Action Tools */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {/* Test Audio Chime */}
          <div
            onClick={handleTestChime}
            className="p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition flex items-start gap-2.5"
          >
            <div className="p-2 rounded-lg bg-blue-100 text-blue-700 shrink-0">
              <Volume2 className={`w-4 h-4 ${testedSound ? 'animate-bounce' : ''}`} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">Test Notification Chime</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {testedSound ? '🔊 Chime playing!' : 'Click to hear audio notification bell'}
              </p>
            </div>
          </div>

          {/* Service Desk Link */}
          <div
            onClick={() => {
              onClose();
              navigate(ROUTES.serviceDesk);
            }}
            className="p-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 cursor-pointer transition flex items-start gap-2.5"
          >
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700 shrink-0">
              <LifeBuoy className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">IT Service Desk</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">Raise IT tickets & technical issues</p>
            </div>
          </div>
        </div>

        {/* Support Banner */}
        <div className="p-3 rounded-xl bg-slate-100/70 border border-slate-200 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-700">
            <BookOpen className="w-4 h-4 text-slate-500" />
            <span>Need full workflow specifications?</span>
          </div>
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate(ROUTES.settings);
            }}
            className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1"
          >
            <span>Open Settings</span>
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>
    </Modal>
  );
}
